
import { useState, useEffect, useRef } from "react";
import { Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AddPixelDialog } from "./AddPixelDialog";
import { EditPixelDialog } from "./pixel/EditPixelDialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Pixel, PixelsSidebarProps } from "./pixel/types";
import { PixelList } from "./pixel/PixelList";
import { SidebarToggle } from "./pixel/SidebarToggle";
import { PixelDeleteAlert } from "./pixel/PixelDeleteAlert";
import { useAuth } from "@/components/AuthProvider";

const PixelsSidebar = ({ 
  pixels, 
  searchTerm, 
  onSearchChange, 
  onPixelSelect, 
  selectedPixel 
}: PixelsSidebarProps) => {
  const [isAddPixelOpen, setIsAddPixelOpen] = useState(false);
  const [pixelToDelete, setPixelToDelete] = useState<Pixel | null>(null);
  const [pixelToEdit, setPixelToEdit] = useState<Pixel | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preventAutoCollapse, setPreventAutoCollapse] = useState(false);
  const addPixelCounterRef = useRef(0); 
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const filteredPixels = pixels?.filter(pixel => 
    pixel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pixel.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPixelClick = () => {
    console.log(`Opening Add Pixel dialog (counter: ${addPixelCounterRef.current})`);
    addPixelCounterRef.current += 1;
    setIsAddPixelOpen(true);
  };

  const toggleSidebar = () => {
    setIsExpanded(prev => !prev);
  };

  useEffect(() => {
    if (!pixels || pixels.length === 0) {
      setIsExpanded(true);
      setPreventAutoCollapse(true);
    } else if (!preventAutoCollapse) {
      setIsExpanded(false);
    }
  }, [pixels, preventAutoCollapse]);

  useEffect(() => {
    if (isExpanded && user?.id) {
      console.log("Sidebar expanded, refreshing pixels data");
      queryClient.invalidateQueries({ queryKey: ['pixels', user.id] });
      
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['pixels', user.id] });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isExpanded, user?.id, queryClient]);

  useEffect(() => {
    if (isExpanded && user?.id) {
      const intervalId = setInterval(() => {
        console.log("Auto-refreshing pixels in sidebar while expanded");
        queryClient.invalidateQueries({ queryKey: ['pixels', user.id] });
      }, 30000);
      
      return () => clearInterval(intervalId);
    }
  }, [isExpanded, user?.id, queryClient]);

  useEffect(() => {
    if (isAddPixelOpen || pixelToEdit || pixelToDelete) {
      setPreventAutoCollapse(true);
      setIsExpanded(true);
    } else {
      const timer = setTimeout(() => {
        setPreventAutoCollapse(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAddPixelOpen, pixelToEdit, pixelToDelete]);

  const handleDeletePixel = async () => {
    if (!pixelToDelete) return;

    try {
      console.log("DEBUG: ===== STARTING PIXEL DELETION PROCESS =====");
      console.log("DEBUG: Pixel to delete:", JSON.stringify(pixelToDelete));
      console.log("DEBUG: Current user:", user ? JSON.stringify({id: user.id, email: user.email}) : "Not logged in");
      
      if (pixelToDelete.organization_id) {
        console.log("DEBUG: Org pixel detected - organization_id:", pixelToDelete.organization_id);
        
        console.log("DEBUG: Fetching organization data to check ownership");
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('created_by, name')
          .eq('id', pixelToDelete.organization_id as any)
          .single();
        
        if (orgError) {
          console.error("DEBUG: Error checking organization ownership:", orgError);
          throw orgError;
        }
        
        console.log("DEBUG: Organization data:", JSON.stringify(orgData));
        
        if (orgData && orgData.created_by !== user?.id) {
          console.log("DEBUG: Permission denied - Organization created_by:", orgData.created_by);
          console.log("DEBUG: Current user ID:", user?.id);
          toast.error("Only the organization owner can delete this pixel");
          setPixelToDelete(null);
          return;
        }
        
        console.log("DEBUG: User is organization owner. Proceeding with deletion.");
      } else if (pixelToDelete.user_id !== user?.id) {
        console.log("DEBUG: Permission denied - Personal pixel");
        console.log("DEBUG: Pixel user_id:", pixelToDelete.user_id);
        console.log("DEBUG: Current user ID:", user?.id);
        toast.error("You don't have permission to delete this pixel");
        setPixelToDelete(null);
        return;
      } else {
        console.log("DEBUG: Personal pixel owned by current user. Proceeding with deletion.");
      }

      console.log(`DEBUG: Calling delete-pixel edge function for pixel ID ${pixelToDelete.id}`);
      
      // Instead of using RPC, call the edge function directly
      const { data: deleteResult, error: deleteError } = await supabase.functions
        .invoke('delete-pixel', {
          body: { pixel_id: pixelToDelete.id }
        });
      
      if (deleteError) {
        console.error("DEBUG: Edge function error:", deleteError);
        throw new Error(`Edge function error: ${deleteError.message}`);
      }

      console.log("DEBUG: Edge function response:", JSON.stringify(deleteResult));
      
      // If the pixel was successfully deleted
      if (deleteResult && deleteResult.success) {
        console.log("DEBUG: Pixel confirmed deleted successfully");
        
        if (selectedPixel?.id === pixelToDelete.id) {
          onPixelSelect(null);
        }

        console.log("DEBUG: Invalidating React Query cache");
        queryClient.invalidateQueries({ queryKey: ['pixels'] });
        
        console.log("DEBUG: Showing success toast");
        toast.success("Pixel deleted successfully");
      } else {
        // If the pixel still exists after deletion attempt
        console.warn("DEBUG: WARNING - Pixel deletion may have failed!");
        toast.error("Failed to delete pixel. Please try again.");
      }
      
      console.log("DEBUG: ===== PIXEL DELETION PROCESS COMPLETE =====");
    } catch (error: any) {
      console.error("DEBUG: Unhandled error in delete pixel function:", error);
      toast.error(`Failed to delete pixel: ${error.message || "Unknown error"}`);
    } finally {
      setPixelToDelete(null);
    }
  };

  return (
    <>
      <aside className={cn(
        "w-72 min-h-screen bg-white border-r border-gray-200 fixed left-64 top-0 transition-transform duration-300 ease-in-out z-10",
        !isExpanded && "-translate-x-full"
      )}>
        <SidebarToggle 
          isExpanded={isExpanded} 
          onClick={toggleSidebar}
          forceDisabled={!pixels || pixels.length === 0}
        />

        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Pixels</h2>
            <Button size="sm" className="gap-1 bg-black hover:bg-black/90" onClick={handleAddPixelClick}>
              <Plus className="w-4 h-4" />
              Add Pixel
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search pixels..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
          
          {filteredPixels && filteredPixels.length > 0 && (
            <PixelList 
              pixels={filteredPixels}
              selectedPixel={selectedPixel}
              onPixelSelect={onPixelSelect}
              onPixelEdit={setPixelToEdit}
              onPixelDelete={setPixelToDelete}
            />
          )}
        </div>
      </aside>

      <AddPixelDialog
        key={`pixel-dialog-${addPixelCounterRef.current}`}
        open={isAddPixelOpen}
        onOpenChange={setIsAddPixelOpen}
      />

      {pixelToEdit && (
        <EditPixelDialog
          open={!!pixelToEdit}
          onOpenChange={(open) => !open && setPixelToEdit(null)}
          pixel={pixelToEdit}
        />
      )}

      <PixelDeleteAlert 
        pixel={pixelToDelete}
        open={!!pixelToDelete}
        onOpenChange={(open) => !open && setPixelToDelete(null)}
        onConfirm={handleDeletePixel}
      />
    </>
  );
};

export default PixelsSidebar;
